import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { actionsAdminAgreement } from '@api/generated/sdk.gen';
import { designTokens } from '@app/theme';
import { usePermissions } from '@refinedev/core';
import { Permissions } from '@shared/constants/permissions';
import { parseApiError } from '@shared/lib/api-error';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Result, Space, Spin, Tabs, Tag, Typography, notification } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { downloadEvidencePackage, useActorPhones } from './api';
import { AuditTrail } from './components/AuditTrail';
import { InstallmentsBlock } from './components/InstallmentsBlock';
import { TransferMatrix } from './components/TransferMatrix';
import { AGREEMENT_STATUS_COLORS, buildConfirmationMatrix, collectActorIds } from './lib';

const { Title, Text } = Typography;

/**
 * Карточка договора в админке. Сейчас единственная вкладка — «Действия»:
 * сводка «кто что подтвердил» для разбора споров поддержкой
 * (`GET /api/admin/agreements/{id}/actions`).
 *
 * Эндпоинт требует оба права: `agreement.read.all` (гейтится роутом)
 * и `audit.read` — без него экран не показывается (403 без запроса).
 * Идентификаторы акторов расшифровываются в телефоны через
 * `GET /api/admin/users/{id}` с кешированием (акторов обычно 2–3).
 *
 * В шапке — кнопка «Выгрузить доказательства» (ZIP с файлами, цепочками
 * аудита и `manifest.json`); рендерится только при праве
 * `dispute.evidence.export`. Пакет собирается на момент запроса и не кешируется.
 */
export function AgreementShowPage() {
  const { t } = useTranslation(['agreements', 'common', 'errors']);
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();
  const { data: perms } = usePermissions<string[]>({});
  const canAudit = perms?.includes(Permissions.AUDIT_READ) ?? false;
  const canExportEvidence = perms?.includes(Permissions.DISPUTE_EVIDENCE_EXPORT) ?? false;

  const evidenceMutation = useMutation({
    mutationFn: () => downloadEvidencePackage(id),
    onSuccess: (filename) => {
      notification.success({
        message: t('agreements:evidence.downloaded', { filename }),
        placement: 'topRight',
      });
    },
    onError: (err: unknown) => {
      const apiErr = parseApiError(
        (err as { response?: { data?: unknown } }).response?.data,
        (err as { response?: { status?: number } }).response?.status,
      );
      notification.error({
        message:
          apiErr.status === 403
            ? t('agreements:evidence.forbidden')
            : t(`errors:${apiErr.code}`, { defaultValue: t('errors:UNKNOWN_ERROR') }),
        placement: 'topRight',
      });
    },
  });

  const actionsQuery = useQuery({
    queryKey: ['admin-agreement-actions', id],
    queryFn: async () => {
      const res = await actionsAdminAgreement({ path: { id } });
      return res.data ?? null;
    },
    enabled: id !== '' && canAudit,
  });

  const data = actionsQuery.data;
  const actorIds = useMemo(() => (data ? collectActorIds(data) : []), [data]);
  const actorPhones = useActorPhones(actorIds);
  const matrix = useMemo(
    () => buildConfirmationMatrix(data?.transferConfirmations ?? []),
    [data?.transferConfirmations],
  );

  if (!canAudit) {
    return (
      <Result
        status="403"
        title={t('common:access.title')}
        subTitle={t('common:access.denied', { permissions: Permissions.AUDIT_READ })}
        extra={
          <Button
            type="primary"
            onClick={() => {
              void navigate('/agreements');
            }}
          >
            {t('agreements:back')}
          </Button>
        }
      />
    );
  }

  if (actionsQuery.isLoading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (actionsQuery.isError || !data) {
    const err = actionsQuery.error as { response?: { data?: unknown; status?: number } } | null;
    const apiErr = parseApiError(err?.response?.data, err?.response?.status);
    return (
      <Card style={{ borderColor: designTokens.slate200 }}>
        <Result
          status={apiErr.status === 404 ? '404' : 'error'}
          title={apiErr.code}
          subTitle={t(`errors:${apiErr.code}`, { defaultValue: t('errors:UNKNOWN_ERROR') })}
          extra={
            <Button
              onClick={() => {
                void navigate('/agreements');
              }}
            >
              {t('agreements:back')}
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space direction="vertical" size={4}>
        <Link to="/agreements" style={{ color: designTokens.slate500, fontSize: 13 }}>
          <ArrowLeftOutlined /> {t('agreements:back')}
        </Link>
        <Space size={12} align="baseline" wrap>
          <Title
            level={3}
            style={{
              margin: 0,
              fontWeight: 600,
              fontFamily: '"JetBrains Mono", monospace',
              letterSpacing: -0.3,
            }}
          >
            {data.reference}
          </Title>
          <Tag
            color={AGREEMENT_STATUS_COLORS[data.status ?? ''] ?? 'default'}
            style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3 }}
          >
            {t(`agreements:statuses.${data.status ?? ''}`, { defaultValue: data.status })}
          </Tag>
          {canExportEvidence && (
            <Button
              icon={<DownloadOutlined />}
              loading={evidenceMutation.isPending}
              onClick={() => {
                if (!evidenceMutation.isPending) evidenceMutation.mutate();
              }}
            >
              {t('agreements:evidence.download')}
            </Button>
          )}
        </Space>
        <Text type="secondary" style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace' }}>
          {data.agreementId}
        </Text>
      </Space>

      <Tabs
        items={[
          {
            key: 'actions',
            label: t('agreements:tabs.actions'),
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <TransferMatrix agreementId={id} matrix={matrix} actorPhones={actorPhones} />
                {(data.installments ?? []).length > 0 && (
                  <InstallmentsBlock
                    installments={data.installments ?? []}
                    actorPhones={actorPhones}
                  />
                )}
                <AuditTrail events={data.events ?? []} actorPhones={actorPhones} />
              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
}
